/**
 * SlimFormReview Component
 *
 * This component displays a SlimForm submission with AI-extracted data,
 * allows editing, and provides approve/reject actions.
 */

import { CORE_URL } from "@/constants/api";
import { VITE_CORE_API_URL } from "@/constants/env";
import { auth } from "@/services/auth-service";
import { selfOrganizationEquipmentAreas } from "@/services/organization-service";
import {
	type ConfidentField,
	type EditedAssetData,
	type SlimFormSubmission,
	approveSlimFormSubmission,
	getSlimFormSubmission,
	rejectSlimFormSubmission,
	reprocessSlimFormSubmission,
	updateSlimFormSubmission,
} from "@/services/slim-form-service";
import {
	type AssetType,
	type Manufacturer,
	getManufacturers,
	getOrganizationAssetTypes,
} from "@/services/utility-service";
import {
	CheckOutlined,
	CloseOutlined,
	EditOutlined,
	SaveOutlined,
	WarningOutlined,
} from "@ant-design/icons";
import {
	Button,
	Card,
	Col,
	Divider,
	Form,
	Image,
	Input,
	Modal,
	Progress,
	Row,
	Select,
	Space,
	Spin,
	Tag,
	message,
} from "antd";
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./SlimFormReview.scss";

interface EquipmentArea {
	id: number;
	name: string;
	isDeprecated?: boolean;
	createdAt?: string;
}

export const SlimFormReview: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const [form] = Form.useForm();

	// State
	const [submission, setSubmission] = useState<SlimFormSubmission | null>(null);
	const [loading, setLoading] = useState(true);
	const [editing, setEditing] = useState(false);
	const [saving, setSaving] = useState(false);
	const [approving, setApproving] = useState(false);
	const [savingAndApproving, setSavingAndApproving] = useState(false);
	const [rejecting, setRejecting] = useState(false);
	const [rejectModalVisible, setRejectModalVisible] = useState(false);
	const [rejectReason, setRejectReason] = useState("");
	const [reprocessing, setReprocessing] = useState(false);
	const [duplicateAssetLocation, setDuplicateAssetLocation] = useState<{
		name: string;
		address?: string;
	} | null>(null);

	// Dropdown data
	const [manufacturers, setManufacturers] = useState<Manufacturer[]>([]);
	const [assetTypes, setAssetTypes] = useState<AssetType[]>([]);
	const [equipmentAreas, setEquipmentAreas] = useState<EquipmentArea[]>([]);

	/**
	 * Load dropdown data on mount
	 */
	useEffect(() => {
		const loadDropdownData = async () => {
			try {
				const [manufacturersData, assetTypesData, equipmentAreasData] =
					await Promise.all([
						getManufacturers(),
						getOrganizationAssetTypes(false),
						selfOrganizationEquipmentAreas(),
					]);

				setManufacturers(manufacturersData);
				setAssetTypes(assetTypesData);
				setEquipmentAreas(equipmentAreasData);
			} catch (error) {
				console.error("Error loading dropdown data:", error);
				message.error("Failed to load dropdown options");
			}
		};

		loadDropdownData();
	}, []);

	/**
	 * Loads the submission data
	 */
	useEffect(() => {
		if (!id) return;

		let isMounted = true;

		const load = async () => {
			if (isMounted) {
				await loadSubmission();
			}
		};

		load();

		return () => {
			isMounted = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	/**
	 * Re-populate form when dropdown data becomes available
	 * ONLY when NOT in editing mode to prevent cursor jumping
	 */
	useEffect(() => {
		// Critical: Don't update form fields while user is actively editing
		if (!submission || editing) return;

		// If we have dropdown data and a submission, repopulate the form to match names to IDs
		if (
			manufacturers.length > 0 ||
			assetTypes.length > 0 ||
			equipmentAreas.length > 0
		) {
			const extractedValues = extractTextValues(submission.extractedData);
			const formData = {
				...extractedValues,
				...(submission.editedData || {}),
			};

			// Look up IDs based on names
			let assetTypeId = formData.organizationAssetTypeId;
			let equipmentAreaId = formData.organizationEquipmentAreaId;
			let manufacturerId = formData.manufacturer_id;

			// Validate pre-resolved IDs against current dropdown options
			if (assetTypeId && assetTypes.length > 0 && !assetTypes.find((at) => at.id === assetTypeId)) {
				assetTypeId = undefined;
			}
			if (equipmentAreaId && equipmentAreas.length > 0 && !equipmentAreas.find((ea) => ea.id === equipmentAreaId)) {
				equipmentAreaId = undefined;
			}

			if (!assetTypeId && formData.assetType && assetTypes.length > 0) {
				const assetTypeName = formData.assetType;
				const matchingType = assetTypes.find(
					(at) => at.name.toLowerCase() === assetTypeName.toLowerCase(),
				);
				if (matchingType) assetTypeId = matchingType.id;
			}

			if (
				!equipmentAreaId &&
				formData.equipmentArea &&
				equipmentAreas.length > 0
			) {
				const equipmentAreaName = formData.equipmentArea;
				const matchingArea = equipmentAreas.find(
					(ea) => ea.name.toLowerCase() === equipmentAreaName.toLowerCase(),
				);
				if (matchingArea) equipmentAreaId = matchingArea.id;
			}

			if (
				!manufacturerId &&
				formData.manufacturer &&
				manufacturers.length > 0
			) {
				const manufacturerName = formData.manufacturer;
				const matchingManufacturer = manufacturers.find(
					(m) => m.name.toLowerCase() === manufacturerName.toLowerCase(),
				);
				if (matchingManufacturer) manufacturerId = matchingManufacturer.id;
			}

			// Only update form with IDs, don't touch other fields
			// Use null to explicitly clear stale values in the Select dropdowns
			form.setFieldsValue({
				organizationAssetTypeId: assetTypeId ?? null,
				organizationEquipmentAreaId: equipmentAreaId ?? null,
				manufacturer_id: manufacturerId,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [manufacturers, assetTypes, equipmentAreas, submission]);

	const loadSubmission = async () => {
		if (!id) return;

		setLoading(true);
		try {
			const data = await getSlimFormSubmission(parseInt(id));
			setSubmission(data);

			// Populate form with extracted data, then merge in edited data
			// Edited data takes precedence for fields that exist
			const extractedValues = extractTextValues(data.extractedData);
			const formData = {
				...extractedValues,
				...(data.editedData || {}),
			};

			// Look up IDs based on names if IDs aren't already set
			let assetTypeId = formData.organizationAssetTypeId;
			let equipmentAreaId = formData.organizationEquipmentAreaId;
			let manufacturerId = formData.manufacturer_id;

			// Validate pre-resolved IDs against current dropdown options
			// (IDs may be stale if the type/area was deprecated after processing)
			if (assetTypeId && assetTypes.length > 0 && !assetTypes.find((at) => at.id === assetTypeId)) {
				console.warn(`[SlimFormReview] Pre-resolved asset type ID ${assetTypeId} not found in current options, clearing`);
				assetTypeId = undefined;
			}
			if (equipmentAreaId && equipmentAreas.length > 0 && !equipmentAreas.find((ea) => ea.id === equipmentAreaId)) {
				console.warn(`[SlimFormReview] Pre-resolved equipment area ID ${equipmentAreaId} not found in current options, clearing`);
				equipmentAreaId = undefined;
			}

			// Try to find asset type ID by name if not already set
			if (!assetTypeId && formData.assetType && assetTypes.length > 0) {
				const assetTypeName = formData.assetType;
				const matchingType = assetTypes.find(
					(at) => at.name.toLowerCase() === assetTypeName.toLowerCase(),
				);
				if (matchingType) {
					assetTypeId = matchingType.id;
				}
			}

			// Try to find equipment area ID by name if not already set
			if (
				!equipmentAreaId &&
				formData.equipmentArea &&
				equipmentAreas.length > 0
			) {
				const equipmentAreaName = formData.equipmentArea;
				const matchingArea = equipmentAreas.find(
					(ea) => ea.name.toLowerCase() === equipmentAreaName.toLowerCase(),
				);
				if (matchingArea) {
					equipmentAreaId = matchingArea.id;
				}
			}

			// Try to find manufacturer ID by name if not already set
			if (
				!manufacturerId &&
				formData.manufacturer &&
				manufacturers.length > 0
			) {
				const manufacturerName = formData.manufacturer;
				const matchingManufacturer = manufacturers.find(
					(m) => m.name.toLowerCase() === manufacturerName.toLowerCase(),
				);
				if (matchingManufacturer) {
					manufacturerId = matchingManufacturer.id;
				}
			}

			// Set form values with both text and ID fields
			form.setFieldsValue({
				...formData,
				// Set ID fields for dropdowns (use null to explicitly clear stale values)
				organizationAssetTypeId: assetTypeId ?? null,
				organizationEquipmentAreaId: equipmentAreaId ?? null,
				manufacturer_id: manufacturerId,
			});

			// Force update editing state to false to ensure we show the latest data
			setEditing(false);
		} catch (error) {
			console.error("Error loading submission:", error);
			message.error(
				error instanceof Error ? error.message : "Failed to load submission",
			);
		} finally {
			setLoading(false);
		}
	};

	/**
	 * Fetches duplicate asset location when rejection reason contains an asset ID
	 */
	useEffect(() => {
		if (!submission?.rejectionReason) {
			setDuplicateAssetLocation(null);
			return;
		}

		// Pattern to match "(ID: 123)" from new format: "Asset Tag: XXX (ID: 123)"
		const assetIdPattern = /\(ID:\s*(\d+)\)/i;
		const match = submission.rejectionReason.match(assetIdPattern);

		if (match && match[1]) {
			const assetId = match[1];
			const fetchAssetLocation = async () => {
				try {
					const url = `${VITE_CORE_API_URL}${CORE_URL}/asset/${assetId}`;
					const response = await fetch(url, {
						method: "GET",
						headers: {
							Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
						},
					});

					if (!response.ok) {
						console.error("Failed to fetch duplicate asset details");
						return;
					}

					const result = await response.json();
					const assetData = result.success ? result.data : result;

					if (assetData?.location) {
						const addressParts = [
							assetData.location.address1,
							assetData.location.city,
							assetData.location.state,
							assetData.location.zip,
						].filter(Boolean);

						setDuplicateAssetLocation({
							name: assetData.location.name || "Unknown Location",
							address:
								addressParts.length > 0 ? addressParts.join(", ") : undefined,
						});
					}
				} catch (error) {
					console.error("Error fetching duplicate asset location:", error);
				}
			};

			fetchAssetLocation();
		}
	}, [submission?.rejectionReason]);

	/**
	 * Extracts text values from ConfidentField objects
	 */
	const extractTextValues = (extractedData: any): Record<string, any> => {
		if (!extractedData) return {};

		const result: Record<string, any> = {};
		Object.keys(extractedData).forEach((key) => {
			const value = extractedData[key];
			if (value && typeof value === "object" && "text" in value) {
				result[key] = value.text;
			} else {
				result[key] = value;
			}
		});
		return result;
	};

	/**
	 * Gets confidence color based on score
	 */
	const getConfidenceColor = (confidence?: number): string => {
		if (!confidence) return "default";
		if (confidence >= 0.8) return "success";
		if (confidence >= 0.6) return "warning";
		return "error";
	};

	/**
	 * Renders a single data field with confidence indicator.
	 * Shared between PROCESSED (read-only) and APPROVED display views.
	 * 
	 * @param label - Display label for the field
	 * @param extractedField - The AI-extracted ConfidentField (may be undefined)
	 * @param editedValue - The user-edited override value (may be undefined)
	 * @param extra - Optional extra JSX to render inside the field value (e.g., manufacturer source tag)
	 */
	const renderDataField = (
		label: string,
		extractedField: ConfidentField | undefined,
		editedValue: string | number | undefined,
		extra?: React.ReactNode,
	) => {
		const displayValue = editedValue || (extractedField ? String(extractedField.text) : null);

		return (
			<div className="data-item">
				<label>{label}:</label>
				{displayValue ? (
					<div className="confident-field">
						<div className="field-value">
							{displayValue}
							{extra}
						</div>
						{extractedField?.confidence != null && (
							<Progress
								percent={Math.round(extractedField.confidence * 100)}
								size="small"
								status={getConfidenceColor(extractedField.confidence) as any}
								format={(percent) => `${percent}%`}
							/>
						)}
					</div>
				) : (
					<div className="field-value empty">Not extracted</div>
				)}
			</div>
		);
	};

	/**
	 * Renders the full data field grid (shared between PROCESSED read-only and APPROVED views).
	 * @param showManufacturerSource - Whether to show "From Plate" / "From Asset Image" tags
	 */
	const renderDataFieldGrid = (showManufacturerSource = false) => {
		if (!submission) return null;

		const ed = submission.editedData;
		const ex = submission.extractedData;

		// Build manufacturer source tag if requested
		let manufacturerExtra: React.ReactNode = null;
		if (showManufacturerSource && ex?.manufacturerSource?.text) {
			manufacturerExtra =
				ex.manufacturerSource.text === "overview" ? (
					<Tag color="blue" style={{ marginLeft: 8 }}>From Asset Image</Tag>
				) : ex.manufacturerSource.text === "plate" ? (
					<Tag color="green" style={{ marginLeft: 8 }}>From Plate</Tag>
				) : null;
		}

		return (
			<Row gutter={[16, 16]}>
				<Col span={12}>{renderDataField("Asset Type", ex?.assetType, ed?.assetType)}</Col>
				<Col span={12}>{renderDataField("Manufacturer", ex?.manufacturer, ed?.manufacturer, manufacturerExtra)}</Col>
				<Col span={12}>{renderDataField("Model Number", ex?.modelNumber, ed?.modelNumber)}</Col>
				<Col span={12}>{renderDataField("Serial Number", ex?.serialNumber, ed?.serialNumber)}</Col>
				<Col span={12}>{renderDataField("Asset Tag", ex?.assetTag, ed?.assetTag)}</Col>
				<Col span={12}>{renderDataField("Condition", ex?.condition, ed?.condition)}</Col>
				<Col span={12}>{renderDataField("Manufacturer Date", ex?.manufacturerDate, ed?.manufacturerDate)}</Col>
				<Col span={12}>{renderDataField("Equipment Area", ex?.equipmentArea, ed?.equipmentArea)}</Col>
				<Col span={12}>{renderDataField("Visible Damage", ex?.visibleDamage, ed?.visibleDamage)}</Col>
				<Col span={24}>
					<div className="data-item">
						<label>General Notes:</label>
						{ed?.generalNotes ? (
							<div className="field-value">{ed.generalNotes}</div>
						) : (
							<div className="field-value empty">No notes</div>
						)}
					</div>
				</Col>
			</Row>
		);
	};

	/**
	 * Saves edited data
	 */
	const handleSave = async () => {
		if (!submission) return;

		try {
			const values = await form.validateFields();
			setSaving(true);

			// Ensure we save both IDs and names for proper display and re-editing
			// IMPORTANT: Preserve existing editedData (like GPS coordinates for Visual Tag 2.0)
			const editedData: EditedAssetData = {
				...(submission.editedData || {}), // Preserve existing data (GPS, etc.)
				...values, // Override with new form values
			};

			// Make sure we have text names for the IDs (for display and re-editing)
			if (values.organizationAssetTypeId && !values.assetType) {
				const type = assetTypes.find(
					(at) => at.id === values.organizationAssetTypeId,
				);
				if (type) editedData.assetType = type.name;
			}

			if (values.organizationEquipmentAreaId && !values.equipmentArea) {
				const area = equipmentAreas.find(
					(ea) => ea.id === values.organizationEquipmentAreaId,
				);
				if (area) editedData.equipmentArea = area.name;
			}

			if (values.manufacturer_id && !values.manufacturer) {
				const mfr = manufacturers.find((m) => m.id === values.manufacturer_id);
				if (mfr) editedData.manufacturer = mfr.name;
			}

			await updateSlimFormSubmission(submission.id, {
				editedData,
				reviewedAt: new Date().toISOString(),
			});

			message.success("Changes saved successfully");
			setEditing(false);
			// Reload submission to show updated data
			await loadSubmission();
		} catch (error) {
			console.error("Error saving changes:", error);
			message.error(
				error instanceof Error ? error.message : "Failed to save changes",
			);
		} finally {
			setSaving(false);
		}
	};

	/**
	 * Saves edited data and approves in one action
	 */
	const handleSaveAndApprove = async () => {
		if (!submission) return;

		try {
			const values = await form.validateFields();
			setSavingAndApproving(true);

			// Ensure we save both IDs and names for proper display and re-editing
			// IMPORTANT: Preserve existing editedData (like GPS coordinates for Visual Tag 2.0)
			const editedData: EditedAssetData = {
				...(submission.editedData || {}), // Preserve existing data (GPS, etc.)
				...values, // Override with new form values
			};

			// Make sure we have text names for the IDs (for display and re-editing)
			if (values.organizationAssetTypeId && !values.assetType) {
				const type = assetTypes.find(
					(at) => at.id === values.organizationAssetTypeId,
				);
				if (type) editedData.assetType = type.name;
			}

			if (values.organizationEquipmentAreaId && !values.equipmentArea) {
				const area = equipmentAreas.find(
					(ea) => ea.id === values.organizationEquipmentAreaId,
				);
				if (area) editedData.equipmentArea = area.name;
			}

			if (values.manufacturer_id && !values.manufacturer) {
				const mfr = manufacturers.find((m) => m.id === values.manufacturer_id);
				if (mfr) editedData.manufacturer = mfr.name;
			}

			// First save the edited data
			await updateSlimFormSubmission(submission.id, {
				editedData,
				reviewedAt: new Date().toISOString(),
			});

			// Then approve and create the asset
			const result = await approveSlimFormSubmission(submission.id);
			message.success("Changes saved and asset created successfully!");
			navigate("/slim-form/queue");
		} catch (error) {
			console.error("Error saving and approving:", error);
			message.error(
				error instanceof Error ? error.message : "Failed to save and approve",
			);
		} finally {
			setSavingAndApproving(false);
		}
	};

	/**
	 * Approves the submission
	 */
	const handleApprove = async () => {
		if (!submission) return;

		Modal.confirm({
			title: "Approve Submission",
			content:
				"Are you sure you want to approve this submission and create the asset?",
			okText: "Approve",
			okType: "primary",
			cancelText: "Cancel",
			onOk: async () => {
				setApproving(true);
				try {
					const result = await approveSlimFormSubmission(submission.id);
					message.success("Asset created successfully!");
					navigate("/slim-form/queue");
				} catch (error) {
					console.error("Error approving submission:", error);
					message.error(
						error instanceof Error
							? error.message
							: "Failed to approve submission",
					);
				} finally {
					setApproving(false);
				}
			},
		});
	};

	/**
	 * Rejects the submission
	 */
	const handleReject = async () => {
		if (!submission || !rejectReason.trim()) {
			message.error("Please provide a reason for rejection");
			return;
		}

		setRejecting(true);
		try {
			await rejectSlimFormSubmission(submission.id, rejectReason);
			message.success("Submission rejected");
			setRejectModalVisible(false);
			navigate("/slim-form/queue");
		} catch (error) {
			console.error("Error rejecting submission:", error);
			message.error(
				error instanceof Error ? error.message : "Failed to reject submission",
			);
		} finally {
			setRejecting(false);
		}
	};

	/**
	 * Manually triggers reprocessing
	 */
	const handleReprocess = async () => {
		if (!submission) return;

		setReprocessing(true);
		try {
			await reprocessSlimFormSubmission(submission.id);
			message.success("Submission queued for reprocessing");
			setTimeout(() => loadSubmission(), 2000);
		} catch (error) {
			console.error("Error reprocessing submission:", error);
			message.error(
				error instanceof Error
					? error.message
					: "Failed to reprocess submission",
			);
		} finally {
			setReprocessing(false);
		}
	};

	/**
	 * Gets status tag
	 */
	const getStatusTag = (status: string) => {
		const statusConfig: Record<string, { color: string; text: string }> = {
			PENDING: { color: "default", text: "Pending" },
			PROCESSING: { color: "processing", text: "Processing" },
			PROCESSED: { color: "success", text: "Ready for Review" },
			APPROVED: { color: "green", text: "Approved" },
			REJECTED: { color: "red", text: "Rejected" },
			FAILED: { color: "error", text: "Failed" },
		};

		const config = statusConfig[status] || { color: "default", text: status };
		return <Tag color={config.color}>{config.text}</Tag>;
	};

	/**
	 * Renders rejection reason with hyperlinks to duplicate asset and location info
	 */
	const renderRejectionReason = (reason: string) => {
		// Pattern to match: "Asset Tag: XXX (ID: 123)" from new format
		// Example: "Duplicate asset tag "ABC123" - already exists as Asset Tag: ABC123 (ID: 456)"
		const assetTagPattern = /Asset Tag:\s*([^\s(]+)\s*\(ID:\s*(\d+)\)/i;
		const match = reason.match(assetTagPattern);

		if (match && match[1] && match[2]) {
			const assetTag = match[1];
			const assetId = match[2];
			const beforeMatch = reason.substring(0, match.index);
			const afterMatch = reason.substring(match.index! + match[0].length);

			return (
				<div>
					<div>
						{beforeMatch}
						<Button
							type="link"
							size="small"
							style={{
								padding: "0 4px",
								height: "auto",
								verticalAlign: "baseline",
							}}
							onClick={() =>
								navigate("/assets", {
									state: { selectedAssetId: assetId },
								})
							}
						>
							Asset Tag: {assetTag}
						</Button>
						{afterMatch}
					</div>
					{duplicateAssetLocation && (
						<div style={{ marginTop: "8px", fontSize: "14px", color: "#666" }}>
							<strong>Location:</strong> {duplicateAssetLocation.name}
							{duplicateAssetLocation.address && (
								<> ({duplicateAssetLocation.address})</>
							)}
						</div>
					)}
				</div>
			);
		}

		return reason;
	};

	if (loading) {
		return (
			<div className="slim-form-review-loading">
				<Spin size="large" tip="Loading submission..." />
			</div>
		);
	}

	if (!submission) {
		return (
			<div className="slim-form-review-error">
				<p>Submission not found</p>
				<Button onClick={() => navigate("/slim-form/queue")}>
					Back to Queue
				</Button>
			</div>
		);
	}

	return (
		<div className="slim-form-review-container">
			<Card
				title={`SlimForm Review - Submission #${submission.id}`}
				extra={
					<Space>
						{getStatusTag(submission.status)}
						<Button onClick={() => navigate("/slim-form/queue")}>
							Back to Queue
						</Button>
					</Space>
				}
				className="review-card"
			>
				{/* Header Info */}
				<div className="submission-info">
					<Row gutter={16}>
						<Col span={8}>
							<strong>Location:</strong>{" "}
							{submission.locationAddress?.split(",")[0]?.trim() ||
								submission.locationName ||
								"-"}
						</Col>
						<Col span={8}>
							<strong>Submitted by:</strong> {submission.userName}
						</Col>
						<Col span={8}>
							<strong>Created:</strong>{" "}
							{new Date(submission.createdAt).toLocaleString()}
						</Col>
					</Row>
					{(submission.assetId || submission.assetTag) && (
						<Row gutter={16} style={{ marginTop: 16 }}>
							{submission.assetTag && (
								<Col span={8}>
									<strong>Asset Tag:</strong> {submission.assetTag}
								</Col>
							)}
						</Row>
					)}
				</div>

				<Divider />

				{/* Photos - Always show */}
				<div className="photos-section">
					<h3>Photos</h3>
					<Row gutter={16}>
						<Col span={8}>
							<Card size="small" title="Overview">
								<Image src={submission.overviewPhotoUrl} alt="Overview" />
							</Card>
						</Col>
						{submission.manufacturerPlatePhotoUrl && (
							<Col span={8}>
								<Card size="small" title="Manufacturer Plate">
									<Image
										src={submission.manufacturerPlatePhotoUrl}
										alt="Manufacturer Plate"
									/>
								</Card>
							</Col>
						)}
						{submission.assetTagPhotoUrl && (
							<Col span={8}>
								<Card size="small" title="Asset Tag">
									<Image src={submission.assetTagPhotoUrl} alt="Asset Tag" />
								</Card>
							</Col>
						)}
					</Row>
				</div>

				<Divider />

				{/* Extracted Data */}
				{submission.status === "PROCESSED" && (
					<>
						<div className="extracted-data-section">
							<div className="section-header">
								<h3>AI-Extracted Data</h3>
								{!editing && submission.status === "PROCESSED" && (
									<Button
										icon={<EditOutlined />}
										onClick={() => setEditing(true)}
									>
										Edit Data
									</Button>
								)}
							</div>

							{!editing ? (
								<div className="data-display">
									{renderDataFieldGrid()}
								</div>
							) : (
								<Form form={form} layout="vertical" className="edit-form">
									<Row gutter={16}>
										<Col span={12}>
											<Form.Item
												label="Asset Type"
												name="organizationAssetTypeId"
											>
												<Select
													placeholder="Select asset type"
													showSearch
													filterOption={(input, option) =>
														(option?.label ?? "")
															.toLowerCase()
															.includes(input.toLowerCase())
													}
													options={assetTypes.map((at) => ({
														label: at.name,
														value: at.id,
													}))}
													onChange={(value) => {
														// Also store the name for display
														const selectedType = assetTypes.find(
															(at) => at.id === value,
														);
														form.setFieldValue("assetType", selectedType?.name);
													}}
												/>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												label="Equipment Area"
												name="organizationEquipmentAreaId"
											>
												<Select
													placeholder="Select equipment area"
													showSearch
													filterOption={(input, option) =>
														(option?.label ?? "")
															.toLowerCase()
															.includes(input.toLowerCase())
													}
													options={equipmentAreas.map((ea) => ({
														label: ea.name,
														value: ea.id,
													}))}
													onChange={(value) => {
														// Also store the name for display
														const selectedArea = equipmentAreas.find(
															(ea) => ea.id === value,
														);
														form.setFieldValue(
															"equipmentArea",
															selectedArea?.name,
														);
													}}
												/>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item label="Manufacturer" name="manufacturer_id">
												<Select
													placeholder="Select manufacturer"
													showSearch
													filterOption={(input, option) =>
														(option?.label ?? "")
															.toLowerCase()
															.includes(input.toLowerCase())
													}
													options={manufacturers.map((m) => ({
														label: m.name,
														value: m.id,
													}))}
													onChange={(value) => {
														// Also store the name for display
														const selectedManufacturer = manufacturers.find(
															(m) => m.id === value,
														);
														form.setFieldValue(
															"manufacturer",
															selectedManufacturer?.name,
														);
													}}
												/>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item label="Model Number" name="modelNumber">
												<Input placeholder="Model number from plate" />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item label="Serial Number" name="serialNumber">
												<Input placeholder="Serial number from plate" />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item label="Asset Tag" name="assetTag">
												<Input placeholder="Asset tag ID" />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item label="Condition" name="condition">
												<Select placeholder="Select condition">
													<Select.Option value="Excellent">
														Excellent
													</Select.Option>
													<Select.Option value="Good">Good</Select.Option>
													<Select.Option value="Fair">Fair</Select.Option>
													<Select.Option value="Poor">Poor</Select.Option>
												</Select>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												label="Manufacturer Date"
												name="manufacturerDate"
											>
												<Input placeholder="YYYY-MM-DD" />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												label="Refrigerant Type"
												name="refrigerantType"
											>
												<Input placeholder="e.g., R-410A, R-22" />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												label="Refrigerant Quantity"
												name="refrigerantQuantity"
											>
												<Input placeholder="e.g., 4.2" />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												label="Power Supply Voltage"
												name="powerSupplyVoltage"
											>
												<Input placeholder="e.g., 208/230" />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												label="Power Supply Amps"
												name="powerSupplyAmps"
											>
												<Input placeholder="e.g., 12.5" />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item label="Visible Damage" name="visibleDamage">
												<Input placeholder="Description of any damage" />
											</Form.Item>
										</Col>
										<Col span={24}>
											<Form.Item label="General Notes" name="generalNotes">
												<Input.TextArea
													rows={3}
													placeholder="Additional notes about this asset"
												/>
											</Form.Item>
										</Col>
									</Row>

									<Space>
										<Button
											icon={<SaveOutlined />}
											onClick={handleSave}
											loading={saving}
											disabled={savingAndApproving}
										>
											Save
										</Button>
										<Button
											type="primary"
											icon={<CheckOutlined />}
											onClick={handleSaveAndApprove}
											loading={savingAndApproving}
											disabled={saving}
										>
											Save & Approve
										</Button>
										<Button
											onClick={() => setEditing(false)}
											disabled={saving || savingAndApproving}
										>
											Cancel
										</Button>
									</Space>
								</Form>
							)}
						</div>

						<Divider />

						{/* Actions */}
						<div className="actions-section">
							<Space size="large">
								<Button
									type="primary"
									icon={<CheckOutlined />}
									size="large"
									onClick={handleApprove}
									loading={approving}
									disabled={submission.status !== "PROCESSED"}
								>
									Approve & Create Asset
								</Button>
								<Button
									danger
									icon={<CloseOutlined />}
									size="large"
									onClick={() => setRejectModalVisible(true)}
									loading={rejecting}
									disabled={submission.status !== "PROCESSED"}
								>
									Reject
								</Button>
								<Button onClick={() => navigate("/slim-form/queue")}>
									Back to Queue
								</Button>
							</Space>
						</div>
					</>
				)}

				{/* Pending Status */}
				{submission.status === "PENDING" && (
					<div className="processing-status">
						<Spin size="large" />
						<h3>Waiting for Processing</h3>
						<p>
							The submission is in the queue. Processing will begin
							automatically.
						</p>
						<p className="help-text">
							<strong>Note:</strong> If stuck, ensure the backend server is
							running and Redis is connected.
						</p>
						<Space>
							<Button type="primary" onClick={loadSubmission}>
								Refresh Status
							</Button>
							<Button onClick={handleReprocess} loading={reprocessing}>
								Retry Processing
							</Button>
						</Space>
					</div>
				)}

				{/* Processing Status */}
				{submission.status === "PROCESSING" && (
					<div className="processing-status">
						<Spin size="large" />
						<p>AI is analyzing the photos... This may take a few minutes.</p>
						<Space>
							<Button onClick={loadSubmission}>Refresh</Button>
							<Button onClick={() => navigate("/slim-form/queue")}>
								Back to Queue
							</Button>
						</Space>
					</div>
				)}

				{/* Failed Status */}
				{submission.status === "FAILED" && (
					<div className="failed-status">
						<WarningOutlined style={{ fontSize: 48, color: "#ff4d4f" }} />
						<h3>Processing Failed</h3>
						<p>
							{submission.errorMessage || "An error occurred during processing"}
						</p>
						{submission.errorDetails && (
							<details>
								<summary>Error Details</summary>
								<pre>{JSON.stringify(submission.errorDetails, null, 2)}</pre>
							</details>
						)}
						<Space>
							<Button
								type="primary"
								onClick={handleReprocess}
								loading={reprocessing}
							>
								Retry Processing
							</Button>
							<Button onClick={() => navigate("/slim-form/queue")}>
								Back to Queue
							</Button>
						</Space>
					</div>
				)}

				{/* Approved Status - Show data without edit/action buttons */}
				{submission.status === "APPROVED" && (
					<>
						<Divider />
						<div className="extracted-data-section">
							<div className="section-header">
								<h3>AI-Extracted Data</h3>
							</div>

							<div className="data-display">
								{renderDataFieldGrid(true)}
							</div>
						</div>

						<Divider />

						<div className="final-status">
							<p>
								This submission has been approved.
								{submission.assetTag && <> Asset Tag: {submission.assetTag}</>}
							</p>
							<Button onClick={() => navigate("/slim-form/queue")}>
								Back to Queue
							</Button>
						</div>
					</>
				)}

				{/* Rejected Status */}
				{submission.status === "REJECTED" && (
					<div className="final-status">
						<p>
							This submission has been rejected.
							{submission.rejectionReason && (
								<>
									{" "}
									Reason: {renderRejectionReason(submission.rejectionReason)}
								</>
							)}
						</p>
						<Button onClick={() => navigate("/slim-form/queue")}>
							Back to Queue
						</Button>
					</div>
				)}
			</Card>

			{/* Reject Modal */}
			<Modal
				title="Reject Submission"
				open={rejectModalVisible}
				onOk={handleReject}
				onCancel={() => setRejectModalVisible(false)}
				confirmLoading={rejecting}
				okText="Reject"
				okButtonProps={{ danger: true }}
			>
				<Form layout="vertical">
					<Form.Item label="Reason for rejection" required>
						<Input.TextArea
							rows={4}
							value={rejectReason}
							onChange={(e) => setRejectReason(e.target.value)}
							placeholder="Enter the reason for rejecting this submission..."
						/>
					</Form.Item>
				</Form>
			</Modal>
		</div>
	);
};
